import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { safeFormatDate } from '../../../shared';
import { Customer } from '../../../types';
import { useCustomerKBService, CustomerKBDocument, CustomerKBSyncJob } from '../services/customerKBService';
import type { ApiErrorResponse } from '../../../types/frontend';

export const knowledgeKeys = {
  // Keyed by customer so a customer switch is a distinct cache entry.
  documents: (customerId: string) => ['knowledge', 'documents', customerId] as const,
};

const errorText = (err: unknown, fallback: string): string =>
  (err as ApiErrorResponse).response?.data?.error || fallback;

interface UseCustomerKBOptions {
  isOpen: boolean;
  customer: Customer | null;
}

export function useCustomerKB({ isOpen, customer }: UseCustomerKBOptions) {
  const kbService = useCustomerKBService();
  const queryClient = useQueryClient();

  const customerId = customer?.id;
  const isKBConfigured = customer?.knowledge_base_id && customer?.knowledge_base_bucket;

  // Action errors (upload/delete/sync/download) are dismissible local state, reset
  // per customer; the list-load error is derived from the query so an action can't
  // clobber it and it clears itself once the load recovers. Action error wins when
  // both are present.
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [currentSyncJob, setCurrentSyncJob] = useState<CustomerKBSyncJob | null>(null);
  const [docToDelete, setDocToDelete] = useState<CustomerKBDocument | null>(null);

  useEffect(() => {
    setActionError(null);
  }, [customerId]);

  const query = useQuery({
    queryKey: knowledgeKeys.documents(customerId ?? ''),
    queryFn: () => kbService.listDocuments(customerId!),
    enabled: isOpen && !!customerId && !!isKBConfigured,
  });

  const documents = query.data?.documents ?? [];
  const totalSize = query.data?.total_size_bytes ?? 0;
  const loadError = query.isError ? errorText(query.error, 'Failed to load documents') : null;
  const error = actionError ?? loadError;

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: knowledgeKeys.documents(customerId ?? '') });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const info = await kbService.getUploadUrl(customerId!, file.name, file.type || 'application/octet-stream');
      await kbService.uploadDocument(info.upload_url, info.fields, file);
      return file.name;
    },
    onMutate: () => setActionError(null),
    onSuccess: (filename) => {
      setSuccessMessage(`Document "${filename}" uploaded successfully`);
      invalidate();
    },
    onError: (err) => setActionError(errorText(err, 'Failed to upload document')),
  });

  const deleteMutation = useMutation({
    mutationFn: async (doc: CustomerKBDocument) => {
      await kbService.deleteDocument(customerId!, doc.document_id);
      return doc.filename;
    },
    onMutate: () => setActionError(null),
    onSuccess: (filename) => {
      setSuccessMessage(`Document "${filename}" deleted`);
      invalidate();
    },
    onError: (err) => setActionError(errorText(err, 'Failed to delete document')),
  });

  const syncMutation = useMutation({
    mutationFn: () => kbService.startSync(customerId!),
    onMutate: () => setActionError(null),
    // Sync is one-shot: it kicks off a background job and returns a handle. It
    // does not change the document list, so there is nothing to invalidate.
    onSuccess: (job) => {
      setCurrentSyncJob(job);
      setSuccessMessage(`Sync started (Job ID: ${job.job_id})`);
    },
    onError: (err) => setActionError(errorText(err, 'Failed to start sync')),
  });

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !customerId) return;
    try {
      await uploadMutation.mutateAsync(file);
    } catch {
      // surfaced via onError
    } finally {
      event.target.value = '';
    }
  };

  const handleDownload = async (doc: CustomerKBDocument) => {
    if (!customerId) return;
    try {
      const result = await kbService.getDownloadUrl(customerId, doc.document_id);
      window.open(result.download_url, '_blank');
    } catch (err) {
      setActionError(errorText(err, 'Failed to get download URL'));
    }
  };

  const handleDelete = (doc: CustomerKBDocument) => {
    setDocToDelete(doc);
  };

  const executeDelete = () => {
    if (!customerId || !docToDelete) return;
    const doc = docToDelete;
    setDocToDelete(null);
    deleteMutation.mutate(doc);
  };

  const handleSync = () => {
    if (!customerId) return;
    syncMutation.mutate();
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return safeFormatDate(dateString, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const clearError = () => setActionError(null);
  const clearSuccess = () => setSuccessMessage(null);

  return {
    documents,
    totalSize,
    loading: query.isFetching,
    uploading: uploadMutation.isPending,
    syncing: syncMutation.isPending,
    error,
    successMessage,
    currentSyncJob,
    isKBConfigured,
    docToDelete,
    setDocToDelete,
    handleFileUpload,
    handleDownload,
    handleDelete,
    executeDelete,
    handleSync,
    formatBytes,
    formatDate,
    clearError,
    clearSuccess,
  };
}
